import { Editor, IAllProps } from '@tinymce/tinymce-react';
import { Editor as tinymce, EditorEvent } from 'tinymce';
import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { getTinyMCELocale } from './localized_editor';
import AiAssistantPopper from './components/ai_assistant/ai_assistant_popper';
import useAiAssistant, { AiAssistant } from './hooks/useAiAssistant';
import { AI_PRODUCT } from './lib/consts';
import InsertSchedulingLinkModal from './components/generic_booking_links/insert_scheduling_link_modal';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { withLocalePrefix } from '../shared/utils/translation';
import InsertWebLinkModal from './components/generic_booking_links/insert_web_link_modal';
import LinkContextMenu from './components/generic_booking_links/link_context_menu';

const t = withLocalePrefix('generic_booking.tinymce_editor');

type IAllPropsWithAi = IAllProps & {
  aiProduct?: AI_PRODUCT;
  showSchedulingLinksMenu?: boolean;
  schedulingLinks?: Array<Item>;
};

export type EditorRefHandle = {
  triggerAction: (actionName: string, toneName?: string) => void;
};

const LocalizedEditorWithAi = forwardRef<EditorRefHandle, IAllPropsWithAi>(
  (props: IAllPropsWithAi, ref) => {
    const { onChange, initialValue, ...restProps } = props;
    const value = useRef(initialValue);
    const anchorEl = useRef<HTMLDivElement>(null);
    const editorRef = useRef<tinymce | null>(null);
    const [
      showSchedulingLinkModal,
      openSchedulingLinkModal,
      closeSchedulingLinkModal,
    ] = useModal();
    const [showWebLinkModal, openWebLinkModal, closeWebLinkModal] = useModal();
    const [isEditingLink, setIsEditingLink] = React.useState(false);
    const [editingLinkData, setEditingLinkData] = React.useState<{
      href: string;
      text: string;
    } | null>(null);
    const [showLinkContextMenu, setShowLinkContextMenu] = React.useState(false);
    const [linkContextMenuData, setLinkContextMenuData] = React.useState<{
      href: string;
      rect: DOMRect | null;
    } | null>(null);

    // TODO: There should be a better way to do this
    const setModalContext = (isEditing: boolean) => {
      setIsEditingLink(isEditing);

      const selectedText = editorRef.current?.selection.getContent({
        format: 'text',
      });

      setEditingLinkData(
        selectedText ? { href: '', text: selectedText } : null
      );
    };

    const unsetModalContext = () => {
      setIsEditingLink(false);
      setEditingLinkData(null);
    };

    // Handler to show the context menu when a link is clicked
    const handleShowLinkContextMenu = (href: string, rect: DOMRect) => {
      setLinkContextMenuData({ href, rect });
      setShowLinkContextMenu(true);
    };
    const handleHideLinkContextMenu = () => {
      setShowLinkContextMenu(false);
      setLinkContextMenuData(null);
    };
    const handleEditLink = () => {
      if (!linkContextMenuData) {
        return;
      }
      const href = linkContextMenuData.href;
      // Find the link element in the editor by href (best effort)
      const editor = editorRef.current;
      if (editor) {
        const links = editor.dom.select('a[href]');
        const linkElm = links.find(
          (l: HTMLElement) => l.getAttribute('href') === href
        );
        if (linkElm) {
          const text = linkElm.innerText || '';
          const linkClass = linkElm.getAttribute('class') || '';
          setEditingLinkData({ href, text });
          setIsEditingLink(true);
          setShowLinkContextMenu(false);
          if (linkClass.includes('scheduling-link')) {
            openSchedulingLinkModal();
          } else {
            openWebLinkModal();
          }
        }
      }
    };
    const handleRemoveLink = () => {
      if (editorRef.current && linkContextMenuData) {
        const links = editorRef.current.dom.select('a[href]');
        const linkElm = links.find(
          (l: HTMLElement) =>
            l.getAttribute('href') === linkContextMenuData.href
        );
        if (linkElm) {
          editorRef.current.dom.remove(linkElm, true);
        }
        setShowLinkContextMenu(false);
      }
    };
    const handleCopyLink = () => {
      if (linkContextMenuData?.href) {
        navigator.clipboard.writeText(linkContextMenuData.href);
      }
    };

    // Listen for link clicks in TinyMCE
    React.useEffect(() => {
      if (!editorRef.current) {
        return;
      }
      const editor = editorRef.current;
      const handleClick = (e: EditorEvent<MouseEvent>) => {
        const target = e.target as HTMLElement;
        if (target && target.nodeName === 'A' && target.getAttribute('href')) {
          e.preventDefault();

          // Get bounding rect relative to the editor container
          const iframe = editor.iframeElement;
          let rect = target.getBoundingClientRect();
          if (iframe && anchorEl.current) {
            const iframeRect = iframe.getBoundingClientRect();
            const containerRect = anchorEl.current.getBoundingClientRect();
            rect = new window.DOMRect(
              rect.left + iframeRect.left - containerRect.left,
              rect.top + iframeRect.top - containerRect.top,
              rect.width,
              rect.height
            );
          }
          handleShowLinkContextMenu(target.getAttribute('href') || '', rect);
        }
      };
      editor.on('click', handleClick);
      return () => {
        editor.off('click', handleClick);
      };
    }, [editorRef]);

    const {
      action,
      selectedTone,
      selectedText,
      editor,
      selectedNode,
      showPopper,
      popperOptions,
      registerAiMenu,
      onAction,
      close,
    }: AiAssistant = useAiAssistant();

    useImperativeHandle(ref, () => ({
      triggerAction: (actionName: string, toneName?: string) => {
        if (editorRef.current) {
          onAction(editorRef.current, actionName, toneName);
        }
      },
    }));

    const setup = (tinyEditor: tinymce) => {
      editorRef.current = tinyEditor;

      registerAiMenu(tinyEditor);
      if (typeof props.init?.setup === 'function') {
        props.init.setup(tinyEditor);
      }

      if (!!props.showSchedulingLinksMenu) {
        tinyEditor.ui.registry.addMenuButton('custom_link_menu', {
          icon: 'link',
          fetch: (callback) => {
            const items = [
              {
                type: 'menuitem' as const,
                text: t('insert_web_link_modal.title.insert'),
                onAction: () => {
                  setModalContext(false);
                  openWebLinkModal();
                },
              },
              {
                type: 'menuitem' as const,
                text: t('insert_scheduling_link_modal.title.insert'),
                onAction: () => {
                  setModalContext(false);
                  openSchedulingLinkModal();
                },
              },
            ];
            callback(items);
          },
        });
      }
    };

    const insertLink = (
      href: string,
      text?: string,
      linkType: 'web' | 'scheduling' = 'web'
    ) => {
      if (!editorRef.current) {
        return;
      }

      // Both editing and inserting use the same updateLink function
      updateLink(editorRef.current, href, text || href, '', '_blank', linkType);
    };

    const updateLink = (
      currentEditor: tinymce,
      href: string,
      text: string,
      title?: string,
      target?: string,
      linkType: 'web' | 'scheduling' = 'web'
    ) => {
      const linkElm = currentEditor.dom.getParent(
        currentEditor.selection.getNode(),
        'a[href]'
      ) as HTMLElement;

      if (linkElm) {
        // Update attributes
        currentEditor.dom.setAttrib(linkElm, 'href', href);
        title !== undefined &&
          currentEditor.dom.setAttrib(linkElm, 'title', title);
        target !== undefined &&
          currentEditor.dom.setAttrib(linkElm, 'target', target);

        // Add link type class
        const linkClass =
          linkType === 'scheduling' ? 'scheduling-link' : 'web-link';
        currentEditor.dom.setAttrib(linkElm, 'class', linkClass);

        // Update link text (optional)
        if (text !== undefined && text !== linkElm.innerText) {
          linkElm.innerText = text;
        }
      } else {
        // If no link is selected, insert a new one instead
        const targetAttr = target
          ? ` target="${currentEditor.dom.encode(target)}"`
          : '';
        const titleAttr = title
          ? ` title="${currentEditor.dom.encode(title)}"`
          : '';
        const linkClass =
          linkType === 'scheduling' ? 'scheduling-link' : 'web-link';
        const classAttr = ` class="${linkClass}"`;

        // Check if there's selected text to wrap
        const selectedContent = currentEditor.selection.getContent();
        const hasSelection = selectedContent && selectedContent.trim() !== '';

        if (hasSelection && (!text || text === href)) {
          // Wrap the selected text with the link (when no custom display text is provided)
          currentEditor.selection.setContent(
            `<a href="${currentEditor.dom.encode(
              href
            )}"${targetAttr}${titleAttr}${classAttr}>${selectedContent}</a>`
          );
        } else {
          // Insert a new link with the provided text
          currentEditor.insertContent(
            `<a href="${currentEditor.dom.encode(
              href
            )}"${targetAttr}${titleAttr}${classAttr}>${currentEditor.dom.encode(
              text
            )}</a>`
          );
        }
      }
    };

    const editorConfig = {
      ...props.init,
      language: getTinyMCELocale(),
      setup: setup,
      toolbar:
        typeof props.init?.toolbar === 'string' &&
        !!props.showSchedulingLinksMenu
          ? props.init.toolbar.replace('link', 'custom_link_menu')
          : props.init?.toolbar,
    };

    return (
      <div className="editor-container" ref={anchorEl}>
        <Editor
          {...restProps}
          onChange={onChange}
          init={editorConfig}
          initialValue={value.current}
        />
        {editor && anchorEl?.current && selectedNode && (
          <AiAssistantPopper
            options={popperOptions}
            action={action}
            selectedText={selectedText}
            selectedTone={selectedTone}
            editor={editor}
            anchorEl={anchorEl.current}
            selectedNode={selectedNode}
            open={showPopper}
            close={close}
            product={props.aiProduct}
          />
        )}
        {props.showSchedulingLinksMenu && (
          <InsertWebLinkModal
            showModal={showWebLinkModal}
            handleClose={() => {
              closeWebLinkModal();
              unsetModalContext();
            }}
            insertLink={(href: string, text?: string) =>
              insertLink(href, text, 'web')
            }
            initialData={editingLinkData}
            isEditing={isEditingLink}
          />
        )}
        {props.showSchedulingLinksMenu && !!props.schedulingLinks && (
          <InsertSchedulingLinkModal
            showModal={showSchedulingLinkModal}
            handleClose={() => {
              closeSchedulingLinkModal();
              unsetModalContext();
            }}
            insertLink={(href: string, text?: string) =>
              insertLink(href, text, 'scheduling')
            }
            schedulingLinksOptions={props.schedulingLinks || []}
            initialData={editingLinkData}
            isEditing={isEditingLink}
          />
        )}
        {linkContextMenuData && linkContextMenuData.rect && (
          <LinkContextMenu
            anchorRect={linkContextMenuData.rect}
            open={showLinkContextMenu}
            linkUrl={linkContextMenuData.href}
            onEdit={handleEditLink}
            onRemove={handleRemoveLink}
            onCopy={handleCopyLink}
            onClose={handleHideLinkContextMenu}
            iframeElement={editorRef.current?.iframeElement}
            container={anchorEl.current}
          />
        )}
      </div>
    );
  }
);

export default LocalizedEditorWithAi;
